import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Button } from "@core/components/ui/button";
import { VsPageLayout } from "./VsPageLayout";

const K = "polls:vs_doodle.";

/** Lignes du tableau : clé de libellé ; les cellules sont `<clé>_doodates` / `<clé>_doodle`. */
const ROWS = [
  "row_price",
  "row_free",
  "row_ads",
  "row_reminders",
  "row_account",
] as const;
const FAQ = ["1", "2"] as const;

export default function VsDoodle() {
  const { t } = useTranslation(["polls"]);
  const k = (key: string) => t(`${K}${key}`);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    name: k("meta_title"),
    mainEntity: FAQ.map((n) => ({
      "@type": "Question",
      name: k(`faq_q${n}`),
      acceptedAnswer: { "@type": "Answer", text: k(`faq_a${n}`) },
    })),
  };

  return (
    <VsPageLayout
      seo={{
        title: k("meta_title"),
        description: k("meta_description"),
        fullTitle: true,
        jsonLd: faqJsonLd,
      }}
      heading={k("heading")}
      intro={k("intro")}
    >
      {/* Tableau comparatif */}
      <section className="mb-16 bg-muted/30 rounded-2xl p-4 sm:p-8 border border-border">
        {/* Mobile : une carte par critère (le tableau à 3 colonnes y serait
            illisible). Le tableau reste la version sémantique dès `sm`. */}
        <dl className="sm:hidden space-y-4" data-testid="vs-doodle-cards">
          {ROWS.map((row) => (
            <div
              key={row}
              className="rounded-xl border border-border bg-background p-4 space-y-2"
            >
              <dt className="font-semibold text-foreground">{k(row)}</dt>
              <dd className="text-sm">
                <span className="block text-xs font-semibold uppercase tracking-wide text-primary">
                  DooDates
                </span>
                {k(`${row}_doodates`)}
              </dd>
              <dd className="text-sm text-muted-foreground">
                <span className="block text-xs font-semibold uppercase tracking-wide">
                  Doodle
                </span>
                {k(`${row}_doodle`)}
              </dd>
            </div>
          ))}
        </dl>
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">{k("table_caption")}</caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="text-left py-3 px-4 font-semibold">
                  {k("col_criterion")}
                </th>
                <th scope="col" className="text-left py-3 px-4 font-semibold">
                  DooDates
                </th>
                <th scope="col" className="text-left py-3 px-4 font-semibold">
                  Doodle
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {ROWS.map((row) => (
                <tr key={row} className="align-top">
                  <th
                    scope="row"
                    className="text-left py-3 px-4 font-medium whitespace-nowrap"
                  >
                    {k(row)}
                  </th>
                  <td className="py-3 px-4 min-w-[14rem]">
                    {k(`${row}_doodates`)}
                  </td>
                  <td className="py-3 px-4 min-w-[14rem] text-muted-foreground">
                    {k(`${row}_doodle`)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          {k("pricing_note")}
        </p>
      </section>

      {/* Pourquoi passer à DooDates */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold mb-4">{k("why_title")}</h2>
        <p className="text-muted-foreground leading-relaxed mb-8">
          {k("why_body")}
        </p>
        <Link to="/create">
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-auto min-h-11 whitespace-normal text-left">
            {k("cta")} <ArrowRight className="w-4 h-4 ml-2 shrink-0" />
          </Button>
        </Link>
      </section>

      {/* FAQ */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-6">{k("faq_title")}</h2>
        <dl className="space-y-6">
          {FAQ.map((n) => (
            <div key={n}>
              <dt className="font-semibold text-foreground mb-2">
                {k(`faq_q${n}`)}
              </dt>
              <dd className="text-muted-foreground leading-relaxed">
                {k(`faq_a${n}`)}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </VsPageLayout>
  );
}
